import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Badge } from "@ui/badge/Badge";
import { HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Input from "@ui/input/Input";
import Panel from "@ui/panel/Panel";
import { Select, type ISelectItemProps } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Rows } from "@ui/table/Rows";
import { Typo } from "@ui/text/Typo";
import Signal from "badmfck-signal";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { notify } from "../../tools/notify";

interface TInviteUserPopupProps {
	onSuccess: () => void;
}

type TInviteRoleOption = {
	id: number;
	name: string;
	description: string;
	value: number;
};

const normalizeInviteRoles = (payload: unknown): TInviteRoleOption[] => {
	const data = payload && typeof payload === "object" && "roles" in payload ? (payload as { roles?: unknown }).roles : payload;
	const roles = Array.isArray(data) ? data : [];
	return roles.flatMap(role => {
		if (!role || typeof role !== "object") return [];

		const rawRole = role as Partial<IUserRole> & { name?: string; description?: string };
		if (typeof rawRole.id !== "number" || !Number.isFinite(rawRole.id)) return [];

		return [
			{
				id: rawRole.id,
				name: rawRole.role ?? rawRole.name ?? msg("templateRole_5cd184f9", { v0: rawRole.id }),
				description: rawRole.description ?? "",
				value: rawRole.id,
			},
		];
	});
};

const InviteRoleSelectItem = ({ value, selected, prompt }: ISelectItemProps<TInviteRoleOption>) => {
	useLocale();
	if (!value) return null;

	return (
		<VBox
			gap="2px"
			sx={{
				padding: prompt ? "6px 10px" : "8px 10px",
				backgroundColor: selected ? "#F8F0ED" : undefined,
				"&:hover": { backgroundColor: prompt ? undefined : "#F8F0ED" },
			}}>
			<Typo sx={{ color: "#1F1D1B", fontSize: "13px", fontWeight: 600, lineHeight: "17px", textTransform: "capitalize" }}>{value.name}</Typo>
			<Typo sx={{ color: "#7B746E", fontSize: "11px", lineHeight: "15px" }}>{value.description}</Typo>
		</VBox>
	);
};

export const S_INVITE_USER_POPUP = new Signal<TInviteUserPopupProps | null>("S_INVITE_USER_POPUP");

export const InviteUserPopup = () => {
	useLocale();
	const request = S_INVITE_USER_POPUP.use();
	//const [addError, setAddError] = useState<IError>();

	const [byEmail, setByEmail] = useState({ value: "" });
	const [busySend, setBusySend] = useState(false);
	const [error, setError] = useState<IError>();
	const [user, setUser] = useState<IUser>();
	const [emailInviteError, setEmailInviteError] = useState<string>();
	const [role, setRole] = useState<TInviteRoleOption | null>(null);
	const [roles, setRoles] = useState<TInviteRoleOption[]>([]);

	// const [users, reloadUsers, _, busy] = GD.REQ_USERS.useRequest(undefined, {
	// 	excludeCurrentProject: true,
	// });

	const [users, setUsers] = useState<IAPIResponse<IUser[]>>();
	const [invites, setInvites] = useState<IAPIResponse<IInvite[]>>();

	// const [invites, _reloadInvites, __, busyInvites] = GD.REQ_INVITES.useRequest(undefined, {
	// 	excludeCurrentProject: true,
	// });

	useEffect(() => {
		if (request) {
			updateUsers();
			updateInvites();
			updateRoles();
		}
	}, [request]);

	const updateUsers = async () => {
		await GD.REQ_USERS.request({ excludeCurrentProject: true });
		//setUsers(req);
		setUsers({ data: [] });
	};

	const updateInvites = async () => {
		const req = await GD.REQ_INVITES.request({});
		setInvites(req);
	};

	const updateRoles = async () => {
		const req = await GD.REQ_API.request({
			method: "users/roles/get",
			data: {
				user_id: 0,
			},
		});

		if (req.error) {
			setError(req.error);
			setRoles([]);
			setRole(null);
			return;
		}

		const options = normalizeInviteRoles(req.data);
		setRoles(options);
		setRole(current => options.find(option => option.value === current?.value) ?? options.find(option => option.value === 7) ?? options[0] ?? null);
	};

	if (!request) return null;

	const closePopup = () => {
		setBusySend(false);
		setByEmail({ value: "" });
		setError(undefined);
		setUser(undefined);
		setInvites(undefined);
		setUsers(undefined);
		setEmailInviteError(undefined);
		setRole(null);
		setRoles([]);
		S_INVITE_USER_POPUP.invoke(null);
	};

	// const onSuccess = () => {
	// 	closePopup();
	// };

	// const addUser = async (user?: IUser) => {
	// 	//!TODO: manager;
	// 	setAddError(undefined);
	// 	const add = await GD.REQ_API.request({
	// 		method: "users/set",
	// 		data: user,
	// 	});
	// 	if (add.error) {
	// 		setAddError(add.error);
	// 	} else {
	// 		onSuccess();
	// 	}
	// };

	const sendInviteSelectedUser = () => {
		if (user) {
			sendInvite(user.email);
		}
	};

	const sendInvite = async (email: string) => {
		setError(undefined);
		if (!role) {
			setError({ code: 0, message: msg("selectARoleBeforeSendingInvitation") });
			return;
		}

		setBusySend(true);
		const req = await GD.REQ_INVITE_USER.request({ email: email, role_id: role.value });
		setBusySend(false);
		if (req.error) {
			setError(req.error);
		} else {
			notify.info(msg("inviteSent"));
			request.onSuccess();
			closePopup();
		}
	};

	const sendInviteByMail = async () => {
		setEmailInviteError(undefined);
		// if (invites?.data) {
		// 	const existInvite = invites.data?.find(i => i.email === byEmail.value && i.status !== "completed");
		// 	if (existInvite) {
		// 		setEmailInviteError("User already invited");
		// 		return;
		// 	}
		// }
		if (byEmail.value) {
			sendInvite(byEmail.value);
		}
	};

	const showUsersList = () => {
		setUser(undefined);
		setError(undefined);
	};

	const cancelInvite = async (i: IInvite) => {
		setBusySend(true);
		const req = await GD.REQ_API.request({
			method: "users/invite/cancel",
			data: {
				token: i.token,
			},
		});
		setBusySend(false);
		if (req.error) {
			setError(req.error);
		} else {
			showUsersList();
			updateUsers();
			updateInvites();
		}
	};

	const existingInvite = user ? invites?.data?.find(i => i.email === user.email) : undefined;

	return (
		<Panel
			busy={busySend}
			contentPadding="0px 20px 20px 20px"
			sx={{
				width: "90vw",
				maxWidth: "600px",
				padding: "0",
				maxHeight: "90vh",
				backgroundColor: "#EEF0F2",
			}}
			decorator="absolute"
			label={msg("inviteUser_19fa22d9")}
			growContent
			onClose={closePopup}>
			<Spacer size="20px" />

			{!user ? (
				<>
					<VBox>
						{emailInviteError && <Typo color="#962424">{emailInviteError}</Typo>}

						{error && (
							<>
								<Error error={error} />
								<Spacer size="12px" />
							</>
						)}

						<HBox gap="12px" sx={{ width: "100%", alignItems: "end" }}>
							<Input name="value" data={byEmail} label={msg("inviteByEmail")} sx={{ flex: 1 }} onChange={() => setEmailInviteError(undefined)} />
						</HBox>
						<Spacer size="12px" />
						<Select<TInviteRoleOption>
							label={msg("role")}
							value={role ?? undefined}
							values={roles}
							item={<InviteRoleSelectItem value={null} />}
							onChange={value => setRole(value)}
							sx={{ width: "100%" }}
						/>
						{/* //!TODO: color; */}
					</VBox>

					{invites && users?.data && users?.data?.length > 0 && (
						<>
							<Spacer size="20px" />
							<Typo>{msg("orSelectExistingUser")}</Typo>
							<Spacer size="10px" />

							<Rows<IUser & { invite?: IInvite }>
								gridTemplateColumns="max-content auto max-content"
								itemType="user"
								searchable={users?.data && users.data.length > 10}
								controlsPadding="20px"
								order={["__itemType", "name", "invite"]}
								data={
									users?.data.map(u => {
										return { ...u, invite: invites.data?.find(i => i.email === u.email) };
									}) ?? []
								}
								onClick={(row, _cell) => {
									setUser(row);
								}}
								cellFactory={(row, _cell, _idx, _key, name, _sx) => {
									if (name === "name") {
										return {
											content: (
												<VBox>
													<Typo sx={{ fontSize: "12px" }}>{row.name}</Typo>
													<Typo>{row.email}</Typo>
												</VBox>
											),
										};
									}
									if (name === "invite") {
										if (row.invite) {
											return { content: <Badge label={row.invite.status}></Badge> };
										} else {
											return { content: <div></div> };
										}
									}
									return { content: null };
								}}
							/>
						</>
					)}
				</>
			) : (
				<>
					<Typo sx={{ paddingTop: "20px", paddingBottom: "20px" }}>
						{existingInvite && existingInvite?.status !== "completed"
							? msg("userAlreadyInvitedCancelCurrentInvite")
							: msg("templateSendInviteTo_429431f5", { v0: user.email })}
					</Typo>
				</>
			)}

			<Spacer size="40px" />

			<HBox gap="12px" sx={{ width: "100%" }}>
				<Spacer grow />
				<Button
					variant={"secondary"}
					title={msg("cancel")}
					onClick={() => {
						user ? showUsersList() : closePopup();
					}}
				/>
				<Button
					variant="primary"
					title={msg("sendInvitation")}
					disabled={!role}
					onClick={() => {
						sendInviteByMail();
					}}
				/>
				{user && (
					<Button
						sx={{ flex: "1" }}
						title={existingInvite && existingInvite?.status !== "completed" ? msg("cancelInvite") : msg("invite")}
						disabled={!(existingInvite && existingInvite?.status !== "completed") && !role}
						onClick={() => {
							existingInvite && existingInvite?.status !== "completed" ? cancelInvite(existingInvite) : sendInviteSelectedUser();
						}}
					/>
				)}
			</HBox>
		</Panel>
	);
};
